import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import { Button } from 'swipeclock-frontend';

const GridBox = styled((props) => <Box {...props} />)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: '1fr',
  width: '100%',
  [theme.breakpoints.up('mobile')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
}));

const FirstTierButton = styled(
  (props) => <Button variant="outlined" {...props} />,
  { shouldForwardProp: (prop) => prop !== 'color' }
)(({ theme, color }) => ({
  height: 64,
  color: color ? color : theme.palette.primary.dark,
  borderColor: color ? color : theme.palette.primary.dark,
  '&:hover': {
    backgroundColor: color ? color : theme.palette.primary.dark,
    borderColor: color ? color : theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
  },
}));

const SecondTierButton = styled(
  (props) => <Button variant="outlined" {...props} />,
  { shouldForwardProp: (prop) => prop !== 'color' }
)(({ theme, color }) => ({
  height: 48,
  color: color ? color : theme.palette.primary.main,
  borderColor: color ? color : theme.palette.primary.main,
  '&:hover': {
    backgroundColor: color ? color : theme.palette.primary.main,
    borderColor: color ? color : theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

const ButtonSkeleton = memo(({ count }) => (
  <GridBox>
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} variant="rectangular" height={i < 2 ? 64 : 48} />
    ))}
  </GridBox>
));

ButtonSkeleton.propTypes = {
  count: PropTypes.number.isRequired,
};

const ButtonPadButton = memo(({ button, isLoading, isDisabled, onClick }) => {
  const Btn = button.tier === 1 ? FirstTierButton : SecondTierButton;

  return (
    <Btn
      id={`sc-button_pad_button_${button.value}`}
      disabled={isDisabled}
      onClick={onClick}
      loading={isLoading}
    >
      {button.label}
    </Btn>
  );
});

ButtonPadButton.propTypes = {
  button: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    tier: PropTypes.number,
  }).isRequired,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

const ButtonGrid = memo(
  ({
    buttonList,
    disabledButtons,
    missingPunches,
    buttonPadLoading,
    buttonLoading,
    onButtonClick,
  }) => (
    <GridBox>
      {buttonList.map((button) => {
        const isLoading = buttonPadLoading && !!buttonLoading[button.value];
        const isDisabled =
          buttonPadLoading ||
          (!missingPunches && !!disabledButtons[button.value]);

        return (
          <ButtonPadButton
            key={button.value}
            button={button}
            isLoading={isLoading}
            isDisabled={isDisabled}
            onClick={onButtonClick(button.value)}
          />
        );
      })}
    </GridBox>
  )
);

ButtonGrid.propTypes = {
  buttonList: PropTypes.array.isRequired,
  disabledButtons: PropTypes.object.isRequired,
  missingPunches: PropTypes.bool.isRequired,
  buttonPadLoading: PropTypes.bool.isRequired,
  buttonLoading: PropTypes.object.isRequired,
  onButtonClick: PropTypes.func.isRequired,
};

export { ButtonSkeleton, ButtonGrid };
